import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { X } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  sheetBodyRecipe,
  sheetFooterRecipe,
  sheetHeaderRecipe,
  sheetRecipe,
  type SheetRecipeProps,
} from '../recipes/sheet.recipe';
import { warnOnUnresponsiveWidth, uiMarkerInternal } from '../recipes/shared';
import {
  dialogBodyRecipe,
  dialogFooterRecipe,
  dialogRecipe,
  overlayBackdropRecipe,
} from '../recipes/overlay.recipe';
import { Text } from './Text';
import { Button } from './Button';

/* ---------------------------------------------------------------- Sheet -- */

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;

export interface SheetContentProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
      'title'
    >,
    SheetRecipeProps {
  /**
   * Heading. Supply this *or* a <SheetTitle> child — without one the sheet has
   * no accessible name, and Radix warns.
   */
  title?: React.ReactNode;
  description?: React.ReactNode;
  hideCloseButton?: boolean;
  footer?: React.ReactNode;
  /**
   * Classes for the backdrop. SheetContent owns its own portal and overlay, so
   * this is how a caller reaches them — the job sheet uses it to suppress the
   * fade, which reads as lag when the panel is opened from a list.
   */
  overlayClassName?: string;
}

/**
 * A panel that slides in from an edge.
 *
 * Use a Sheet to show more about something already on screen — job details,
 * a filter drawer. Use a Dialog when the user has to answer something before
 * carrying on.
 *
 *   <Sheet>
 *     <SheetTrigger asChild><Button>Filters</Button></SheetTrigger>
 *     <SheetContent side="right" title="Filters">…</SheetContent>
 *   </Sheet>
 */
export const SheetContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  SheetContentProps
>(function SheetContent(
  {
    className,
    side,
    size,
    title,
    description,
    hideCloseButton = false,
    footer,
    overlayClassName,
    children,
    ...props
  },
  ref
) {
  // No `title` means the caller is composing the header themselves; see the
  // note on DialogContent about why the aria-describedby opt-out has to be
  // skipped in that case.
  warnOnUnresponsiveWidth('SheetContent', className);

  const composed = title === undefined;

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(overlayBackdropRecipe(), overlayClassName)}
      />
      <DialogPrimitive.Content
        ref={ref}
        {...(composed || description ? {} : { 'aria-describedby': undefined })}
        className={cn(sheetRecipe({ side, size }), className)}
        {...props}
      >
        {composed ? (
          <>
            {children}
            {!hideCloseButton ? (
              <DialogPrimitive.Close asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close"
                  className="absolute right-4 top-4"
                >
                  <X />
                </Button>
              </DialogPrimitive.Close>
            ) : null}
          </>
        ) : (
          <>
            <div className={sheetHeaderRecipe()}>
              <div className="relay-ui-internal min-w-0 space-y-1">
                <DialogPrimitive.Title asChild>
                  <Text variant="h3">{title}</Text>
                </DialogPrimitive.Title>
                {description ? (
                  <DialogPrimitive.Description asChild>
                    <Text variant="body-sm" tone="muted">
                      {description}
                    </Text>
                  </DialogPrimitive.Description>
                ) : null}
              </div>
              {!hideCloseButton ? (
                <DialogPrimitive.Close asChild>
                  <Button variant="ghost" size="icon-sm" aria-label="Close">
                    <X />
                  </Button>
                </DialogPrimitive.Close>
              ) : null}
            </div>

            <div className={sheetBodyRecipe()}>{children}</div>

            {footer ? (
              <div className={sheetFooterRecipe()}>{footer}</div>
            ) : null}
          </>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

/* ---------------------------------------------------------- AlertDialog -- */

/**
 * COMPOSED PARTS for Sheet, for the panels whose header is more than a title
 * and a line of description. Same output as the props form.
 */
export const SheetHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function SheetHeader({ className, ...props }, ref) {
  return (
    <div ref={ref} className={cn(sheetHeaderRecipe(), className)} {...props} />
  );
});

export const SheetTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function SheetTitle({ className, children, ...props }, ref) {
  return (
    <DialogPrimitive.Title ref={ref} asChild {...props}>
      <Text variant="h3" className={className}>
        {children}
      </Text>
    </DialogPrimitive.Title>
  );
});

export const SheetDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function SheetDescription({ className, children, ...props }, ref) {
  return (
    <DialogPrimitive.Description ref={ref} asChild {...props}>
      <Text variant="body-sm" tone="muted" className={className}>
        {children}
      </Text>
    </DialogPrimitive.Description>
  );
});

export const AlertDialog = AlertDialogPrimitive.Root;
export const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

export interface AlertDialogContentProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>,
    'title'
  > {
  /**
   * Heading. Supply this *or* an <AlertDialogTitle> child — leaving it off
   * puts the dialog in composed mode, where you lay the content out yourself.
   */
  title?: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive styling for the confirm button. */
  destructive?: boolean;
  loading?: boolean;
  onConfirm?: () => void;
  /** Replace the default Cancel/Confirm pair entirely. */
  footer?: React.ReactNode;
}

/**
 * A dialog the user has to answer.
 *
 * Deliberately a different Radix primitive from Dialog rather than a styling
 * variant of it. An alert dialog gets `role="alertdialog"`, focuses the safe
 * action first, and — critically — cannot be dismissed by clicking outside or
 * pressing Escape by accident. That is the whole point for "delete this
 * impound?": a stray click must not count as an answer.
 *
 * `ConfirmDialog` is the same shape built on the plain Dialog, kept for the
 * lighter confirmations where an accidental dismiss is harmless.
 */
export const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  AlertDialogContentProps
>(function AlertDialogContent(
  {
    className,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    destructive = false,
    loading = false,
    onConfirm,
    footer,
    children,
    ...props
  },
  ref
) {
  // No `title` means the caller is composing the content themselves with
  // <AlertDialogHeader> / <AlertDialogTitle>, so this renders the panel and
  // gets out of the way.
  const composed = title === undefined;

  return (
    <AlertDialogPrimitive.Portal>
      <AlertDialogPrimitive.Overlay className={overlayBackdropRecipe()} />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(dialogRecipe({ size: 'sm' }), className)}
        {...props}
      >
        {composed ? (
          (() => {
            /*
             * The panel carries no inset (see dialogRecipe), so the composed
             * form has to supply it the way DialogContent does: the footer is
             * hoisted out to sit flush against the panel's edge with its own
             * padding, and everything else goes in a padded body. Without
             * this, every composed alert dialog in the product rendered its
             * title and buttons against the border.
             */
            const kids = React.Children.toArray(children);
            const footerChild = kids.find(
              (k) => React.isValidElement(k) && k.type === AlertDialogFooter
            );
            const rest = kids.filter((k) => k !== footerChild);
            return (
              <>
                <div className={dialogBodyRecipe({ padding: 'md' })}>
                  {rest}
                </div>
                {footerChild}
              </>
            );
          })()
        ) : (
          <>
            <div className={dialogBodyRecipe({ padding: 'md' })}>
              <div className="relay-ui-internal min-w-0 space-y-1">
                <AlertDialogPrimitive.Title asChild>
                  <Text variant="h3">{title}</Text>
                </AlertDialogPrimitive.Title>
                {description ? (
                  <AlertDialogPrimitive.Description asChild>
                    <Text variant="body-sm" tone="muted">
                      {description}
                    </Text>
                  </AlertDialogPrimitive.Description>
                ) : null}
              </div>

              {children ? (
                <div className="relay-ui-internal min-w-0">{children}</div>
              ) : null}
            </div>

            <div className={dialogFooterRecipe()}>
              {footer ?? (
                <>
                  <AlertDialogPrimitive.Cancel asChild>
                    <Button variant="ghost" disabled={loading}>
                      {cancelLabel}
                    </Button>
                  </AlertDialogPrimitive.Cancel>
                  <AlertDialogPrimitive.Action asChild>
                    <Button
                      variant={destructive ? 'destructive' : 'primary'}
                      loading={loading}
                      onClick={(event) => {
                        // Keep the dialog open while the action runs; the caller
                        // closes it once the request settles.
                        if (loading) event.preventDefault();
                        onConfirm?.();
                      }}
                    >
                      {confirmLabel}
                    </Button>
                  </AlertDialogPrimitive.Action>
                </>
              )}
            </div>
          </>
        )}
      </AlertDialogPrimitive.Content>
    </AlertDialogPrimitive.Portal>
  );
});

/**
 * COMPOSED PARTS
 *
 * The other way to build an alert dialog, for the ones whose body is more than
 * a title and a line of description — a list of consequences, a form, a count
 * of what is about to be deleted:
 *
 *   <AlertDialog>
 *     <AlertDialogContent>
 *       <AlertDialogHeader>
 *         <AlertDialogTitle>Release 3 vehicles?</AlertDialogTitle>
 *         <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
 *       </AlertDialogHeader>
 *       <AlertDialogFooter>
 *         <AlertDialogCancel>Keep them</AlertDialogCancel>
 *         <AlertDialogAction onClick={release}>Release</AlertDialogAction>
 *       </AlertDialogFooter>
 *     </AlertDialogContent>
 *   </AlertDialog>
 *
 * Identical output to the props form above. Prefer the props form for a plain
 * "are you sure?"; it cannot be assembled wrong.
 */
export const AlertDialogHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function AlertDialogHeader({ className, ...props }, ref) {
  return (
    <div
      ref={ref}
      className={cn(
        uiMarkerInternal,
        'flex min-w-0 flex-col gap-1 p-4',
        className
      )}
      {...props}
    />
  );
});

export const AlertDialogFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function AlertDialogFooter({ className, ...props }, ref) {
  return (
    <div ref={ref} className={cn(dialogFooterRecipe(), className)} {...props} />
  );
});

export const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(function AlertDialogTitle({ className, children, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Title ref={ref} asChild {...props}>
      <Text variant="h3" className={className}>
        {children}
      </Text>
    </AlertDialogPrimitive.Title>
  );
});

export const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(function AlertDialogDescription({ className, children, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Description ref={ref} asChild {...props}>
      <Text variant="body-sm" tone="muted" className={className}>
        {children}
      </Text>
    </AlertDialogPrimitive.Description>
  );
});

export interface AlertDialogActionProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> {
  /** For an action that deletes, cancels or releases something. */
  destructive?: boolean;
  loading?: boolean;
}

export const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  AlertDialogActionProps
>(function AlertDialogAction(
  { className, children, destructive, loading, ...props },
  ref
) {
  return (
    <AlertDialogPrimitive.Action ref={ref} asChild {...props}>
      <Button
        variant={destructive ? 'destructive' : 'primary'}
        loading={loading}
        className={className}
      >
        {children}
      </Button>
    </AlertDialogPrimitive.Action>
  );
});

export const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(function AlertDialogCancel({ className, children, ...props }, ref) {
  return (
    <AlertDialogPrimitive.Cancel ref={ref} asChild {...props}>
      <Button variant="ghost" className={className}>
        {children}
      </Button>
    </AlertDialogPrimitive.Cancel>
  );
});
